import { 
  Project, 
  EnvVar, 
  CreateProjectRequest, 
  UpdateProjectRequest, 
  BulkEnvVarsRequest,
  ApiResponse,
  LoginRequest,
  LoginResponse,
  AuthResponse
} from '@vault/shared';
import { getAuthToken } from '../contexts/AuthContext';

const BASE_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    includeAuth: boolean = true
  ): Promise<ApiResponse<T>> {
    const url = `${BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add auth token if required and available
    if (includeAuth) {
      const token = getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    
    const config: RequestInit = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Project methods
  async getProjects(): Promise<Project[]> {
    const response = await this.request<Project[]>('/api/projects');
    return response.data || [];
  }

  async getProject(id: number): Promise<Project> {
    const response = await this.request<Project>(`/api/projects/${id}`);
    if (!response.data) {
      throw new Error('Project not found');
    }
    return response.data;
  }

  async createProject(data: CreateProjectRequest): Promise<Project> {
    const response = await this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.data) {
      throw new Error('Failed to create project');
    }
    return response.data;
  }

  async updateProject(id: number, data: UpdateProjectRequest): Promise<Project> {
    const response = await this.request<Project>(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.data) {
      throw new Error('Failed to update project');
    }
    return response.data;
  }

  async deleteProject(id: number): Promise<void> {
    await this.request(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Environment variable methods
  async getProjectEnvVars(projectId: number): Promise<EnvVar[]> {
    const response = await this.request<EnvVar[]>(
      `/api/projects/${projectId}/env-vars`
    );
    return response.data || [];
  }

  async bulkUpsertEnvVars(data: BulkEnvVarsRequest): Promise<EnvVar[]> {
    const response = await this.request<EnvVar[]>('/api/env-vars/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data || [];
  }

  async updateEnvVar(id: number, data: { key?: string; value?: string }): Promise<EnvVar> {
    const response = await this.request<EnvVar>(`/api/env-vars/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    
    if (!response.data) {
      throw new Error('Failed to update environment variable');
    }
    
    return response.data;
  }

  async deleteEnvVar(id: number): Promise<void> {
    await this.request(`/api/env-vars/${id}`, {
      method: 'DELETE',
    });
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }, false); // Don't include auth header for login
    
    if (!response.data) {
      throw new Error('Login failed');
    }
    
    return response.data;
  }

  async verifyAuth(): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/auth/verify');
    
    if (!response.data) {
      throw new Error('Authentication verification failed');
    }
    
    return response.data;
  }
}

export const apiClient = new ApiClient();